import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MapPin, Edit, Trash2, Plus, Wand2, Building2, ChevronDown, Lock, Unlock, User, Calendar, Clock, Loader2, ExternalLink, History } from 'lucide-react';
import { toast } from 'sonner';
import CrearLotesMasivo from '@/components/CrearLotesMasivo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  bg: 'bg-zinc-950', card: 'bg-zinc-900', border: 'border-zinc-800',
  text: 'text-white', textMuted: 'text-zinc-400',
  input: 'bg-zinc-800 border-zinc-700 text-white', hover: 'hover:bg-zinc-800',
};

const initialFormData = {
  numero: '', manzana: '', calle: '', descripcion: '',
  superficie: '', medidas: '', precio_m2: '', precio: '',
  moneda: 'USD', estado: 'disponible', notas: '', costo_terreno: ''
};

const LoteBox = ({ lote, onClick }) => {
  const statusStyles = {
    disponible: 'bg-amber-500 text-black',
    apartado: 'bg-[#D4AF37] text-black',
    vendido: 'bg-zinc-600 text-zinc-400'
  };
  return (
    <div
      data-testid={`lote-${lote.lote_id}`}
      className={`aspect-square rounded-lg ${statusStyles[lote.estado] || statusStyles.disponible}
        cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg
        flex flex-col items-center justify-center font-bold text-sm relative`}
      onClick={() => onClick(lote)}
    >
      {lote.numero}
      {lote.estado === 'apartado' && <Lock className="w-3 h-3 absolute top-1 right-1 opacity-60" />}
    </div>
  );
};

const ManzanaContainer = ({ manzana, lotes, onLoteClick, onAddLotes, canEdit }) => {
  const [expanded, setExpanded] = useState(true);
  const stats = {
    disponibles: lotes.filter(l => l.estado === 'disponible').length,
    apartados: lotes.filter(l => l.estado === 'apartado').length,
    vendidos: lotes.filter(l => l.estado === 'vendido').length
  };
  const maxNumero = Math.max(...lotes.map(l => parseInt(l.numero) || 0), 0);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-800 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-amber-500" />
          <span className="text-white font-bold">Manzana {manzana || '—'}</span>
          <span className="text-zinc-500 text-sm">{lotes.length} lotes</span>
        </div>
        <div className="flex items-center gap-5">
          <div className="hidden md:flex items-center gap-4 text-xs">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-500"></span><span className="text-zinc-400">{stats.disponibles}</span></span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#D4AF37]"></span><span className="text-zinc-400">{stats.apartados}</span></span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-zinc-600"></span><span className="text-zinc-400">{stats.vendidos}</span></span>
          </div>
          <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {expanded && (
        <div className="p-5 pt-0">
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-3">
            {lotes.sort((a, b) => parseInt(a.numero) - parseInt(b.numero)).map(lote => (
              <LoteBox key={lote.lote_id} lote={lote} onClick={onLoteClick} />
            ))}
            {canEdit && (
              <div
                className="aspect-square rounded-lg bg-zinc-800 border-2 border-dashed border-zinc-600
                  cursor-pointer transition-all duration-200 hover:border-amber-500 hover:bg-zinc-700
                  flex items-center justify-center"
                onClick={(e) => { e.stopPropagation(); onAddLotes(manzana, maxNumero + 1); }}
              >
                <Plus className="w-6 h-6 text-zinc-500 hover:text-amber-500" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Lotes() {
  const [user, setUser] = useState(null);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [selectedFracc, setSelectedFracc] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLote, setSelectedLote] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  // Apartar state
  const [apartarDialog, setApartarDialog] = useState(false);
  const [apartarForm, setApartarForm] = useState({ cliente_nombre: '', cliente_telefono: '', firma_fecha: '', firma_hora: '', abono_apartado: '', metodo_abono: '' });
  const [apartarLoading, setApartarLoading] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);
  // Liberar state
  const [liberarDialog, setLiberarDialog] = useState(false);
  const [liberarMotivo, setLiberarMotivo] = useState('');
  // Vender state
  const [venderDialog, setVenderDialog] = useState(false);
  const [venderLoading, setVenderLoading] = useState(false);
  const [venderResult, setVenderResult] = useState(null);
  const [liquidacion, setLiquidacion] = useState({ tipo: 'pago_unico', numero_pagos: '3', notas: '' });
  // Historial state
  const [historialDialog, setHistorialDialog] = useState(false);
  const [historialData, setHistorialData] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) setUser(await res.json());
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  const isComisionista = user?.account_role === 'comisionista' || user?.role === 'comisionista';
  const isAdmin = user?.account_role === 'owner' || user?.account_role === 'admin' || user?.role === 'owner' || user?.role === 'admin';
  const canEdit = !isComisionista; // comisionistas cannot edit/delete/create lotes

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' });
        if (!response.ok) throw new Error('Error');
        const data = await response.json();
        setFraccionamientos(Array.isArray(data) ? data : []);
        if (data.length > 0) {
          const savedFraccId = sessionStorage.getItem('filtro_fraccionamiento');
          let savedFracc = null;
          if (savedFraccId && savedFraccId !== 'todos') {
            savedFracc = data.find(f => f.fraccionamiento_id === savedFraccId);
          }
          const fraccToUse = savedFracc || data[0];
          setSelectedFracc(fraccToUse);
          fetchLotes(fraccToUse.fraccionamiento_id);
        }
      } catch (error) {
        console.error('Error loading fraccionamientos:', error);
        toast.error('Error al cargar fraccionamientos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFracc && fraccionamientos.length > 0) fetchLotes(selectedFracc.fraccionamiento_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFracc]);

  const fetchLotes = async (fraccId) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/lotes?fraccionamiento_id=${fraccId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error');
      const lotesData = await response.json();
      setLotes(Array.isArray(lotesData) ? lotesData : []);
    } catch (error) {
      console.error('Error loading lotes:', error);
      toast.error('Error al cargar lotes');
    } finally {
      setLoading(false);
    }
  };

  const handleLoteClick = (lote) => {
    setSelectedLote(lote);
    setFormData({
      numero: lote.numero || '', manzana: lote.manzana || '', calle: lote.calle || '',
      descripcion: lote.descripcion || '', superficie: lote.superficie?.toString() || '',
      medidas: lote.medidas || '', precio_m2: lote.precio_m2?.toString() || '',
      precio: lote.precio?.toString() || '', estado: lote.estado || 'disponible', notas: lote.notas || '',
      costo_terreno: lote.costo_terreno?.toString() || ''
    });
    setEditMode(false);
    setDetailDialogOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/lotes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          fraccionamiento_id: selectedFracc.fraccionamiento_id, ...formData,
          superficie: formData.superficie ? parseFloat(formData.superficie) : null,
          precio_m2: formData.precio_m2 ? parseFloat(formData.precio_m2) : null,
          precio: formData.precio ? parseFloat(formData.precio) : null,
          costo_terreno: formData.costo_terreno ? parseFloat(formData.costo_terreno) : null
        })
      });
      if (!response.ok) throw new Error('Error');
      toast.success('Lote creado');
      setCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchLotes(selectedFracc.fraccionamiento_id);
    } catch { toast.error('Error al crear lote'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedLote) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/lotes/${selectedLote.lote_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          ...formData,
          superficie: formData.superficie ? parseFloat(formData.superficie) : null,
          precio_m2: formData.precio_m2 ? parseFloat(formData.precio_m2) : null,
          precio: formData.precio ? parseFloat(formData.precio) : null,
          costo_terreno: formData.costo_terreno ? parseFloat(formData.costo_terreno) : null
        })
      });
      if (!response.ok) throw new Error('Error');
      toast.success('Lote actualizado');
      setDetailDialogOpen(false);
      fetchLotes(selectedFracc.fraccionamiento_id);
    } catch { toast.error('Error al actualizar'); }
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/lotes/${deleteDialog.id}`, { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error('Error');
      toast.success('Lote eliminado');
      setDeleteDialog({ open: false, id: null });
      setDetailDialogOpen(false);
      fetchLotes(selectedFracc.fraccionamiento_id);
    } catch { toast.error('Error al eliminar'); }
  };

  // --- APARTAR ---
  const handleApartar = async () => {
    if (!apartarForm.cliente_nombre || !apartarForm.firma_fecha || !apartarForm.firma_hora) {
      toast.error('Nombre del cliente, fecha y hora de firma son obligatorios');
      return;
    }
    setApartarLoading(true);
    try {
      const body = {
        cliente_nombre: apartarForm.cliente_nombre,
        cliente_telefono: apartarForm.cliente_telefono || null,
        firma_fecha: apartarForm.firma_fecha,
        firma_hora: apartarForm.firma_hora,
        abono_apartado: apartarForm.abono_apartado ? parseFloat(apartarForm.abono_apartado) : null,
        metodo_abono: apartarForm.metodo_abono || null
      };
      const res = await fetch(`${BACKEND_URL}/api/lotes/${selectedLote.lote_id}/apartar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setWhatsappLink(data.whatsapp_link);
        setApartarDialog(false);
        setDetailDialogOpen(false);
        setSuccessDialog(true);
        fetchLotes(selectedFracc.fraccionamiento_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al apartar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setApartarLoading(false); }
  };

  // --- LIBERAR ---
  const handleLiberar = async () => {
    setApartarLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lotes/${selectedLote.lote_id}/liberar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ motivo: liberarMotivo || null })
      });
      if (res.ok) {
        toast.success('Lote liberado exitosamente');
        setLiberarDialog(false);
        setDetailDialogOpen(false);
        fetchLotes(selectedFracc.fraccionamiento_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al liberar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setApartarLoading(false); }
  };

  // --- HISTORIAL ---
  const loadHistorial = async (loteId) => {
    setHistorialLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lotes/${loteId}/historial`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHistorialData(data.historial || []);
      }
    } catch { toast.error('Error al cargar historial'); }
    finally { setHistorialLoading(false); }
  };

  const lotesAgrupados = lotes.reduce((acc, lote) => {
    const manzana = lote.manzana || 'Sin Manzana';
    if (!acc[manzana]) acc[manzana] = [];
    acc[manzana].push(lote);
    return acc;
  }, {});

  const stats = {
    total: lotes.length,
    disponibles: lotes.filter(l => l.estado === 'disponible').length,
    apartados: lotes.filter(l => l.estado === 'apartado').length,
    vendidos: lotes.filter(l => l.estado === 'vendido').length
  };

  const [bulkFormData, setBulkFormData] = useState({ manzana: '', desde: '', hasta: '' });

  if (loading && fraccionamientos.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="lotes-page" className="min-h-screen bg-zinc-950 p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-10">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Inventario de Lotes</h1>
          <p className="text-zinc-400 mt-2">
            {isComisionista ? 'Consulta lotes disponibles y aparta para tus clientes' : 'Administra los lotes de tus fraccionamientos'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isComisionista && fraccionamientos.length > 0 && (
            <Select
              value={selectedFracc?.fraccionamiento_id || ''}
              onValueChange={(value) => {
                const f = fraccionamientos.find(fr => fr.fraccionamiento_id === value);
                setSelectedFracc(f);
                localStorage.setItem('filtro_fraccionamiento', value);
                setSelectedLote(null); setDetailDialogOpen(false); setEditMode(false); setFormData(initialFormData);
              }}
            >
              <SelectTrigger data-testid="select-fraccionamiento" className={`w-[220px] ${t.input}`}>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className={`${t.card} border ${t.border}`}>
                {fraccionamientos.map((f) => (
                  <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className={`${t.text} ${t.hover}`}>{f.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canEdit && (
            <>
              <Button onClick={() => setBulkDialogOpen(true)} variant="outline" className={`${t.border} ${t.text} ${t.hover}`} disabled={!selectedFracc}>
                <Wand2 className="w-4 h-4 mr-2" />Crear Masivo
              </Button>
              <Button onClick={() => { setFormData(initialFormData); setCreateDialogOpen(true); }} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" disabled={!selectedFracc}>
                <Plus className="w-4 h-4 mr-2" />Nuevo Lote
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div className={`${t.card} border ${t.border} rounded-xl p-5`}>
          <p className="text-zinc-400 text-sm mb-1">Total</p>
          <p className="text-3xl font-bold text-white font-mono">{stats.total}</p>
        </div>
        <div className={`${t.card} border border-amber-500/30 rounded-xl p-5`}>
          <p className="text-amber-400 text-sm mb-1">Disponibles</p>
          <p className="text-3xl font-bold text-amber-500 font-mono">{stats.disponibles}</p>
        </div>
        <div className={`${t.card} border border-[#C9A24D]/30 rounded-xl p-5`}>
          <p className="text-[#D4AF37] text-sm mb-1">Apartados</p>
          <p className="text-3xl font-bold text-[#D4AF37] font-mono">{stats.apartados}</p>
        </div>
        <div className={`${t.card} border ${t.border} rounded-xl p-5`}>
          <p className="text-zinc-400 text-sm mb-1">Vendidos</p>
          <p className="text-3xl font-bold text-zinc-400 font-mono">{stats.vendidos}</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-8 text-sm">
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-amber-500"></div><span className={t.textMuted}>Disponible</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#D4AF37]"></div><span className={t.textMuted}>Apartado</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-zinc-600"></div><span className={t.textMuted}>Vendido</span></div>
      </div>

      {/* Grid */}
      {lotes.length === 0 ? (
        <div className={`${t.card} border ${t.border} rounded-xl p-16 text-center`}>
          <MapPin className="w-16 h-16 mx-auto mb-4 text-zinc-400" />
          <h3 className="text-lg font-bold text-white mb-2">No hay lotes registrados</h3>
          <p className="text-zinc-400 mb-4">Crea lotes para comenzar</p>
          {canEdit && (
            <Button onClick={() => setBulkDialogOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" disabled={!selectedFracc}>
              <Wand2 className="w-4 h-4 mr-2" />Crear Masivo
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(lotesAgrupados).sort(([a], [b]) => a.localeCompare(b)).map(([manzana, lotesM]) => (
            <ManzanaContainer
              key={manzana} manzana={manzana} lotes={lotesM} onLoteClick={handleLoteClick} canEdit={canEdit}
              onAddLotes={(manzanaName, nextNum) => {
                setBulkFormData({ ...bulkFormData, manzana: manzanaName, desde: nextNum.toString(), hasta: (nextNum + 4).toString() });
                setBulkDialogOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* ========== MODAL: Detalle de Lote ========== */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`sm:max-w-md ${t.card} border ${t.border} ${t.text}`}>
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              Lote #{selectedLote?.numero}
              {selectedLote?.manzana && <span className="text-zinc-400 font-normal">Mzna. {selectedLote.manzana}</span>}
            </DialogTitle>
          </DialogHeader>

          {!editMode ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                  selectedLote?.estado === 'disponible' ? 'bg-amber-500/20 text-amber-400' :
                  selectedLote?.estado === 'apartado' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                  'bg-zinc-700 text-zinc-400'
                }`}>
                  {selectedLote?.estado?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500">Superficie</p>
                  <p className="font-mono font-bold text-white">{selectedLote?.superficie || 'N/A'} m²</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Precio ({selectedLote?.moneda || 'USD'})</p>
                  <p className="font-mono font-bold text-amber-400">{selectedLote?.moneda || 'USD'} ${selectedLote?.precio?.toLocaleString('es-MX') || 'N/A'}</p>
                </div>
                {selectedLote?.precio_m2 && (
                  <div>
                    <p className="text-xs text-zinc-500">Precio por m²</p>
                    <p className="font-mono text-emerald-400">{selectedLote?.moneda || 'USD'} ${selectedLote.precio_m2?.toLocaleString('es-MX')} /m²</p>
                  </div>
                )}
                {selectedLote?.medidas && (
                  <div>
                    <p className="text-xs text-zinc-500">Medidas</p>
                    <p className="text-white">{selectedLote.medidas}</p>
                  </div>
                )}
              </div>

              {/* Apartado info */}
              {selectedLote?.estado === 'apartado' && (
                <div data-testid="apartado-info" className="bg-[#C6A54A]/10 border border-[#C6A54A]/20 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-[#C6A54A] font-semibold text-sm">
                    <Lock className="w-4 h-4" /> Apartado
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedLote.apartado_por_nombre && (
                      <div><span className="text-zinc-500">Por:</span> <span className="text-white">{selectedLote.apartado_por_nombre}</span></div>
                    )}
                    {selectedLote.cliente_nombre && (
                      <div><span className="text-zinc-500">Cliente:</span> <span className="text-white">{selectedLote.cliente_nombre}</span></div>
                    )}
                    {selectedLote.firma_programada && (
                      <div className="col-span-2">
                        <span className="text-zinc-500">Firma:</span>{' '}
                        <span className="text-[#C6A54A] font-medium">
                          {new Date(selectedLote.firma_programada).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}{' '}
                          {new Date(selectedLote.firma_programada).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                    {selectedLote.apartado_expira && (
                      <div className="col-span-2">
                        <span className="text-zinc-500">Expira:</span>{' '}
                        <span className="text-red-400 text-xs">
                          {new Date(selectedLote.apartado_expira).toLocaleDateString('es-MX')} {new Date(selectedLote.apartado_expira).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Abono info */}
              {selectedLote?.abono_apartado > 0 && (
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4" data-testid="abono-apartado-info">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Abono de Apartado</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-400">Monto recibido:</span>
                      <p className="text-cyan-400 font-mono font-bold text-lg">{selectedLote.moneda || 'USD'} ${selectedLote.abono_apartado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    </div>
                    {selectedLote.metodo_abono && (
                      <div>
                        <span className="text-zinc-400">Método de pago:</span>
                        <p className="text-white font-medium">{({efectivo:'Efectivo',transferencia:'Transferencia',cheque:'Cheque',tarjeta:'Tarjeta'})[selectedLote.metodo_abono] || selectedLote.metodo_abono}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vendido info - solo admin */}
              {selectedLote?.estado === 'vendido' && isAdmin && selectedLote.vendido_por_nombre && (
                <div data-testid="venta-info" className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Información de Venta</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-zinc-500">Vendido por:</span> <span className="text-white">{selectedLote.vendido_por_nombre}</span></div>
                    <div><span className="text-zinc-500">Comisión:</span> <span className="text-[#C6A54A]">{selectedLote.porcentaje_comision}%</span></div>
                    <div><span className="text-zinc-500">Monto comisión:</span> <span className="text-red-400">${selectedLote.comision_monto?.toLocaleString('es-MX')}</span></div>
                    <div><span className="text-zinc-500">Ganancia:</span> <span className="text-emerald-400 font-bold">${selectedLote.ganancia_estimada?.toLocaleString('es-MX')}</span></div>
                  </div>
                </div>
              )}

              {/* Costo terreno - solo admin */}
              {isAdmin && selectedLote?.costo_terreno > 0 && (
                <div className="p-3 bg-zinc-800/50 rounded-lg text-sm">
                  <span className="text-zinc-500">Costo terreno:</span>{' '}
                  <span className="text-zinc-300">{selectedLote.moneda || 'USD'} ${selectedLote.costo_terreno?.toLocaleString('es-MX')}</span>
                </div>
              )}

              {selectedLote?.notas && (
                <div className="p-3 bg-zinc-800 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Notas</p>
                  <p className="text-sm text-zinc-300">{selectedLote.notas}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                {/* Historial button */}
                <Button
                  data-testid="historial-btn"
                  variant="outline"
                  onClick={() => { loadHistorial(selectedLote.lote_id); setHistorialDialog(true); }}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <History className="w-4 h-4 mr-2" />Historial
                </Button>
                {/* Apartar button - visible when lote is disponible */}
                {selectedLote?.estado === 'disponible' && (
                  <Button
                    data-testid="apartar-btn"
                    onClick={() => {
                      setApartarForm({ cliente_nombre: '', cliente_telefono: '', firma_fecha: '', firma_hora: '', abono_apartado: '', metodo_abono: '' });
                      setApartarDialog(true);
                    }}
                    className="flex-1 bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold"
                  >
                    <Lock className="w-4 h-4 mr-2" />Apartar
                  </Button>
                )}
                {/* Liberar button - only admin/owner when apartado */}
                {selectedLote?.estado === 'apartado' && isAdmin && (
                  <>
                    <Button
                      data-testid="liberar-btn"
                      onClick={() => { setLiberarMotivo(''); setLiberarDialog(true); }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                    >
                      <Unlock className="w-4 h-4 mr-2" />Liberar
                    </Button>
                    <Button
                      data-testid="vender-btn"
                      onClick={() => setVenderDialog(true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    >
                      Confirmar Venta
                    </Button>
                  </>
                )}
                {/* Edit/Delete - only admin (not comisionista) */}
                {canEdit && (
                  <>
                    <Button onClick={() => setEditMode(true)} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold">
                      <Edit className="w-4 h-4 mr-2" />Editar
                    </Button>
                    <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      onClick={() => setDeleteDialog({ open: true, id: selectedLote?.lote_id })}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Número</Label><Input value={formData.numero} onChange={(e) => setFormData({...formData, numero: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                <div><Label className="text-zinc-300">Manzana</Label><Input value={formData.manzana} onChange={(e) => setFormData({...formData, manzana: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Superficie (m²)</Label><Input type="number" step="0.01" value={formData.superficie} onChange={(e) => setFormData({...formData, superficie: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                <div><Label className="text-zinc-300">Precio ({selectedLote?.moneda || 'USD'})</Label><Input type="number" step="0.01" value={formData.precio} onChange={(e) => setFormData({...formData, precio: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              </div>
              <div>
                <Label className="text-zinc-300">Estado</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData({...formData, estado: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="disponible" className="text-white hover:bg-zinc-700">Disponible</SelectItem>
                    <SelectItem value="apartado" className="text-white hover:bg-zinc-700">Apartado</SelectItem>
                    <SelectItem value="vendido" className="text-white hover:bg-zinc-700">Vendido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-300">Notas</Label><Textarea value={formData.notas} onChange={(e) => setFormData({...formData, notas: e.target.value})} rows={2} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditMode(false)} className="flex-1 border-zinc-700 text-white hover:bg-zinc-800">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold">Guardar</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Apartar Lote ========== */}
      <Dialog open={apartarDialog} onOpenChange={setApartarDialog}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#C6A54A]">
              <Lock className="w-5 h-5" /> Apartar Lote #{selectedLote?.numero}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Ingresa los datos del cliente para apartar este lote
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-zinc-800/70 rounded-lg p-3 text-sm">
              <span className="text-zinc-400">Precio:</span>{' '}
              <span className="text-[#C6A54A] font-bold">{selectedLote?.moneda || 'USD'} ${selectedLote?.precio?.toLocaleString('es-MX')}</span>
              <span className="text-zinc-500 ml-3">{selectedLote?.superficie} m²</span>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Nombre del Cliente *</Label>
              <Input data-testid="apartar-cliente-nombre" value={apartarForm.cliente_nombre}
                onChange={e => setApartarForm(p => ({...p, cliente_nombre: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" placeholder="Nombre completo" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Teléfono</Label>
              <Input data-testid="apartar-cliente-telefono" value={apartarForm.cliente_telefono}
                onChange={e => setApartarForm(p => ({...p, cliente_telefono: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" placeholder="+52 55 1234 5678" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 text-sm">Fecha de Firma *</Label>
                <Input data-testid="apartar-firma-fecha" type="date" value={apartarForm.firma_fecha}
                  onChange={e => setApartarForm(p => ({...p, firma_fecha: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Hora *</Label>
                <Input data-testid="apartar-firma-hora" type="time" value={apartarForm.firma_hora}
                  onChange={e => setApartarForm(p => ({...p, firma_hora: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 text-sm">Abono Recibido</Label>
                <Input data-testid="apartar-abono" type="number" step="0.01" value={apartarForm.abono_apartado}
                  onChange={e => setApartarForm(p => ({...p, abono_apartado: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-white" placeholder="0.00" />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Método de Pago</Label>
                <Select value={apartarForm.metodo_abono} onValueChange={v => setApartarForm(p => ({...p, metodo_abono: v}))}>
                  <SelectTrigger data-testid="apartar-metodo" className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="efectivo" className="text-white">Efectivo</SelectItem>
                    <SelectItem value="transferencia" className="text-white">Transferencia</SelectItem>
                    <SelectItem value="cheque" className="text-white">Cheque</SelectItem>
                    <SelectItem value="tarjeta" className="text-white">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setApartarDialog(false)} className="flex-1 text-zinc-400">Cancelar</Button>
              <Button data-testid="confirm-apartar-btn" onClick={handleApartar} disabled={apartarLoading} className="flex-1 bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold">
                {apartarLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Confirmar Apartado
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Éxito + WhatsApp ========== */}
      <Dialog open={successDialog} onOpenChange={setSuccessDialog}>
        <DialogContent className="sm:max-w-sm bg-zinc-900 border-zinc-800 text-white text-center">
          <div className="space-y-4 py-4">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Lote Apartado</h3>
            <p className="text-sm text-zinc-400">El lote ha sido apartado exitosamente</p>
            {whatsappLink && (
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors">
                <ExternalLink className="w-4 h-4" />
                Enviar WhatsApp al cliente
              </a>
            )}
            <Button variant="ghost" onClick={() => setSuccessDialog(false)} className="text-zinc-400 w-full">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Liberar ========== */}
      <Dialog open={liberarDialog} onOpenChange={setLiberarDialog}>
        <DialogContent className="sm:max-w-sm bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-400">
              <Unlock className="w-5 h-5" /> Liberar Lote
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              El lote volverá a estar disponible. Esta acción quedará registrada en el historial.
            </p>
            <div>
              <Label className="text-zinc-300 text-sm">Motivo (opcional)</Label>
              <Textarea data-testid="liberar-motivo" value={liberarMotivo}
                onChange={e => setLiberarMotivo(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white resize-none" rows={2}
                placeholder="Ej: Cliente se arrepintió" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setLiberarDialog(false)} className="flex-1 text-zinc-400">Cancelar</Button>
              <Button data-testid="confirm-liberar-btn" onClick={handleLiberar} disabled={apartarLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold">
                {apartarLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                Confirmar Liberación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Confirmar Venta ========== */}
      <Dialog open={venderDialog} onOpenChange={setVenderDialog}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">Confirmar Venta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {venderResult ? (
              <div data-testid="venta-result" className="space-y-3">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-center text-white font-semibold">Venta Registrada</p>
                <div className="bg-zinc-800 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-400">Vendido por</span><span className="text-white">{venderResult.vendido_por}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Comisión ({venderResult.porcentaje_comision}%)</span><span className="text-[#C6A54A]">${venderResult.comision_monto?.toLocaleString('es-MX')}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Liquidación</span><span className="text-white capitalize">{venderResult.tipo_liquidacion === 'enganche' ? 'Pagada con enganche' : venderResult.tipo_liquidacion === 'pagos' ? `En ${venderResult.numero_pagos} pagos` : 'Pago único'}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">Estado comisión</span><span className={venderResult.comision_estado === 'pagada' ? 'text-emerald-400' : 'text-amber-400'}>{venderResult.comision_estado === 'pagada' ? 'Pagada' : 'Pendiente'}</span></div>
                  <div className="flex justify-between border-t border-zinc-700 pt-2"><span className="text-zinc-400 font-medium">Ganancia</span><span className="text-emerald-400 font-bold">${venderResult.ganancia_estimada?.toLocaleString('es-MX')}</span></div>
                </div>
                <Button onClick={() => { setVenderDialog(false); setVenderResult(null); }} className="w-full bg-zinc-800 text-white">Cerrar</Button>
              </div>
            ) : (
              <>
                <div className="bg-zinc-800 rounded-lg p-3 text-sm">
                  <p className="text-white font-medium">Lote #{selectedLote?.numero} {selectedLote?.manzana ? `Mz ${selectedLote.manzana}` : ''}</p>
                  <p className="text-[#C6A54A]">{selectedLote?.moneda || 'USD'} ${selectedLote?.precio?.toLocaleString('es-MX')}</p>
                  {selectedLote?.apartado_por_nombre && <p className="text-zinc-400 text-xs mt-1">Comisionista: {selectedLote.apartado_por_nombre}</p>}
                </div>

                {/* Liquidación de Comisión */}
                <div className="border border-zinc-800 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-[#C6A54A] uppercase tracking-wider">Liquidación de Comisión</h3>
                  <div className="space-y-2">
                    <label data-testid="liq-enganche" className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-all ${liquidacion.tipo === 'enganche' ? 'border-[#C6A54A] bg-[#C6A54A]/10' : 'border-zinc-800 hover:border-zinc-700'}`}
                      onClick={() => setLiquidacion(p => ({...p, tipo: 'enganche'}))}>
                      <input type="radio" name="liquidacion" checked={liquidacion.tipo === 'enganche'} readOnly className="mt-1 accent-[#C6A54A]" />
                      <div>
                        <p className="text-white text-sm font-medium">Pagada con el enganche</p>
                        <p className="text-xs text-zinc-500">La comisión se marca como pagada inmediatamente</p>
                      </div>
                    </label>
                    <label data-testid="liq-pago-unico" className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-all ${liquidacion.tipo === 'pago_unico' ? 'border-[#C6A54A] bg-[#C6A54A]/10' : 'border-zinc-800 hover:border-zinc-700'}`}
                      onClick={() => setLiquidacion(p => ({...p, tipo: 'pago_unico'}))}>
                      <input type="radio" name="liquidacion" checked={liquidacion.tipo === 'pago_unico'} readOnly className="mt-1 accent-[#C6A54A]" />
                      <div>
                        <p className="text-white text-sm font-medium">Pago único manual</p>
                        <p className="text-xs text-zinc-500">Se registrará el pago manualmente después</p>
                      </div>
                    </label>
                    <label data-testid="liq-pagos" className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-all ${liquidacion.tipo === 'pagos' ? 'border-[#C6A54A] bg-[#C6A54A]/10' : 'border-zinc-800 hover:border-zinc-700'}`}
                      onClick={() => setLiquidacion(p => ({...p, tipo: 'pagos'}))}>
                      <input type="radio" name="liquidacion" checked={liquidacion.tipo === 'pagos'} readOnly className="mt-1 accent-[#C6A54A]" />
                      <div>
                        <p className="text-white text-sm font-medium">En pagos programados</p>
                        <p className="text-xs text-zinc-500">Dividir la comisión en pagos mensuales</p>
                      </div>
                    </label>
                    {liquidacion.tipo === 'pagos' && (
                      <div className="ml-9 mt-1">
                        <label className="text-xs text-zinc-400">Número de pagos</label>
                        <input data-testid="liq-numero-pagos" type="number" min="2" max="24"
                          value={liquidacion.numero_pagos}
                          onChange={e => setLiquidacion(p => ({...p, numero_pagos: e.target.value}))}
                          className="w-24 mt-1 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setVenderDialog(false)} className="flex-1 text-zinc-400">Cancelar</Button>
                  <Button data-testid="confirm-vender-btn" onClick={async () => {
                    setVenderLoading(true);
                    try {
                      const res = await fetch(`${BACKEND_URL}/api/lotes/${selectedLote.lote_id}/vender`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                          tipo_liquidacion: liquidacion.tipo,
                          numero_pagos: liquidacion.tipo === 'pagos' ? parseInt(liquidacion.numero_pagos) || 3 : null,
                          notas_liquidacion: liquidacion.notas || null
                        })
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setVenderResult(data);
                        toast.success('Venta registrada');
                        setDetailDialogOpen(false);
                        fetchLotes(selectedFracc.fraccionamiento_id);
                      } else {
                        const err = await res.json();
                        toast.error(err.detail || 'Error');
                      }
                    } catch { toast.error('Error de conexión'); }
                    finally { setVenderLoading(false); }
                  }} disabled={venderLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                    {venderLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Confirmar Venta
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Individual */}
      {canEdit && (
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader><DialogTitle className="text-white">Crear Lote</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Número *</Label><Input value={formData.numero} onChange={(e) => setFormData({...formData, numero: e.target.value})} required className="bg-zinc-800 border-zinc-700 text-white" /></div>
                <div><Label className="text-zinc-300">Manzana</Label><Input value={formData.manzana} onChange={(e) => setFormData({...formData, manzana: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-zinc-300">Superficie (m²)</Label><Input type="number" step="0.01" value={formData.superficie} onChange={(e) => setFormData({...formData, superficie: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                <div><Label className="text-zinc-300">Medidas</Label><Input value={formData.medidas} onChange={(e) => setFormData({...formData, medidas: e.target.value})} placeholder="Ej: 10x20" className="bg-zinc-800 border-zinc-700 text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Moneda</Label>
                  <Select value={formData.moneda} onValueChange={(v) => setFormData({...formData, moneda: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="USD" className="text-white hover:bg-zinc-700">USD</SelectItem>
                      <SelectItem value="MXN" className="text-white hover:bg-zinc-700">MXN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300">Precio ({formData.moneda})</Label><Input type="number" step="0.01" value={formData.precio} onChange={(e) => setFormData({...formData, precio: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              </div>
              <div><Label className="text-zinc-300">Notas</Label><Textarea value={formData.notas} onChange={(e) => setFormData({...formData, notas: e.target.value})} rows={2} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              <div><Label className="text-zinc-300">Costo Terreno ({formData.moneda})</Label><Input type="number" step="0.01" value={formData.costo_terreno || ''} onChange={(e) => setFormData({...formData, costo_terreno: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Opcional" /></div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">Crear Lote</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* ========== MODAL: Historial del Lote ========== */}
      <Dialog open={historialDialog} onOpenChange={setHistorialDialog}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#C6A54A]" />
              Historial — Lote #{selectedLote?.numero}
            </DialogTitle>
          </DialogHeader>
          {/* Quick action: Liberar if apartado */}
          {isAdmin && selectedLote?.estado === 'apartado' && (
            <div data-testid="historial-liberar-action" className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-400 font-medium">Lote actualmente apartado</p>
                <p className="text-xs text-zinc-400">{selectedLote.apartado_por_nombre || 'Comisionista'}</p>
              </div>
              <Button data-testid="historial-liberar-btn" size="sm"
                onClick={() => { setHistorialDialog(false); setLiberarDialog(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8">
                Liberar Lote
              </Button>
            </div>
          )}
          {historialLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#C6A54A] animate-spin" /></div>
          ) : historialData.length === 0 ? (
            <p className="text-center text-zinc-500 py-8">Sin registros de historial</p>
          ) : (
            <div data-testid="historial-timeline" className="relative pl-6 space-y-4">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-zinc-700" />
              {historialData.map((h, i) => {
                const tipoColors = {
                  apartado: 'bg-[#C6A54A]', liberado: 'bg-blue-500',
                  expirado: 'bg-red-500', convertido_venta: 'bg-emerald-500'
                };
                const tipoLabels = {
                  apartado: 'Apartado', liberado: 'Liberado',
                  expirado: 'Expirado', convertido_venta: 'Vendido'
                };
                return (
                  <div key={h.historial_id || i} className="relative">
                    <div className={`absolute -left-4 w-3 h-3 rounded-full ${tipoColors[h.tipo] || 'bg-zinc-500'} ring-4 ring-zinc-900`} />
                    <div className="bg-zinc-800/70 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${
                          h.tipo === 'apartado' ? 'text-[#C6A54A]' :
                          h.tipo === 'liberado' ? 'text-blue-400' :
                          h.tipo === 'expirado' ? 'text-red-400' : 'text-emerald-400'
                        }`}>{tipoLabels[h.tipo] || h.tipo}</span>
                        <span className="text-xs text-zinc-500">
                          {new Date(h.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}{' '}
                          {new Date(h.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-white mt-1">{h.usuario_nombre}</p>
                      {h.cliente_nombre && <p className="text-xs text-zinc-400">Cliente: {h.cliente_nombre}</p>}
                      {h.firma_programada && (
                        <p className="text-xs text-zinc-400">
                          Firma: {new Date(h.firma_programada).toLocaleDateString('es-MX')} {new Date(h.firma_programada).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      {h.abono > 0 && <p className="text-xs text-emerald-400">Abono: ${h.abono?.toLocaleString('es-MX')}{h.metodo_abono ? ` (${h.metodo_abono})` : ''}</p>}
                      {h.motivo && <p className="text-xs text-zinc-500 italic mt-1">{h.motivo}</p>}
                      {h.comisionista_nombre && <p className="text-xs text-zinc-400">Comisionista: {h.comisionista_nombre}</p>}
                      {h.comision_monto > 0 && <p className="text-xs text-[#C6A54A]">Comisión: ${h.comision_monto?.toLocaleString('es-MX')}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk create */}
      <CrearLotesMasivo open={bulkDialogOpen} onOpenChange={setBulkDialogOpen} fraccionamiento={selectedFracc} onSuccess={() => fetchLotes(selectedFracc.fraccionamiento_id)} />

      {/* Delete confirm */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar lote?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
